import { clearEpgCache } from '../api/epgCache';
import { clearSeriesInfoInflight } from './seriesInfoCache';
import { invalidateSeriesStreamsCache } from './seriesStreamsCache';
import { clearVodInfoInflight } from './vodInfoCache';
import { invalidateVodStreamsCache } from './vodStreamsCache';
import { clearContentIdentityCache, CONTENT_IDENTITY_CACHE_PREFIX } from './contentIdentityResolver';
import xtreamAPI from '../api/xtreamAPI';
import { invalidateLiveStreamsCache } from './liveStreamsCache';
import { catalogHashService } from '../services/catalogHashService';

/**
 * Cache management utilities.
 *
 * Product-level operations:
 *   clearCatalogCache     — technical catalog, metadata, images, search and EPG
 *   clearPlaybackLocalData — progress, history, favorites and recent live TV
 */

const PRESERVE_EXACT = new Set([
  'ott_player_auth_data',
  'ott_device_v1',
  'ott_hw_uid',
  'ott_playlist_activation_url_v1',
  'ott_lang',
  'ott_tmdb_lang',
  'ott_tmdb_region',
  'ott_player_schedules',
  'ott_parental_controls_v1',
  'ott_default_page',
  'ott_movie_autoplay_mode',
  'ott_content_data_source',
  'ott_live_engine',
  'ott_live_buffer_minutes',
  'privacy_consent_accepted',
  'i18nextLng',
]);

const PRESERVE_PREFIXES = [
  'ott_player_favorites:',
  'ott_player_watch_progress:',
  'ott_player_history:',
  'ott_player_live_history:',
  'ott_watch_sync_v2:',
  'ott_profile_storage_migrated_v2:',
  CONTENT_IDENTITY_CACHE_PREFIX,
];

/** Clears origin Cache Storage, including image responses saved by a worker/runtime. */
export async function clearBrowserCacheStorage(): Promise<void> {
  const storage = (window as any).caches;
  if (!storage?.keys || !storage?.delete) return;
  try {
    const names: string[] = await storage.keys();
    await Promise.all(names.map((name) => storage.delete(name)));
  } catch {
    // Cache Storage is optional/partially implemented on older TVs.
  }
}

/**
 * Clears all content/cache keys while preserving auth, the active profile,
 * favorites, watch progress, live history, and user settings.
 * Used when changing language.
 */
export async function clearCatalogCache(): Promise<void> {
  invalidateLiveStreamsCache();
  invalidateVodStreamsCache();
  invalidateSeriesStreamsCache();
  catalogHashService.clear();
  clearVodInfoInflight();
  clearSeriesInfoInflight();
  // Identity associations are server-scoped; preserve other configured servers.
  clearContentIdentityCache(xtreamAPI.getHost());
  const keys = Object.keys(localStorage);
  for (const key of keys) {
    if (PRESERVE_EXACT.has(key)) continue;
    if (PRESERVE_PREFIXES.some((p) => key.startsWith(p))) continue;
    localStorage.removeItem(key);
  }
  await Promise.all([clearEpgCache(), clearBrowserCacheStorage()]);
}

/**
 * Clears VOD/series metadata caches AND all TMDB list caches
 * (movies, series, kids, anime, top, on-air, now-playing, national, genres).
 */
export function clearMoviesMetadataCache(): void {
  const TMDB_PREFIXES = [
    // Per-item metadata
    'ott_vod_info_v1_',
    'ott_series_info_v1_',
    CONTENT_IDENTITY_CACHE_PREFIX,
    // Page-level caches (pages/Movies, pages/Series, pages/Kids, pages/Anime)
    'ott_movies_v',
    'ott_series_v',
    'ott_kids_page_v',
    'ott_anime_page_v',
    // ContentList source cache
    'ott_list_',
    // tmdbAPI bulk-fetch caches
    'ott_kids_movies_',
    'ott_kids_series_',
    'ott_anime_series_',
    'ott_anime_movies_',
    'ott_top_movies_',
    'ott_top_series_',
    'ott_on_air_',
    'ott_now_playing_',
    'ott_recent_movies_',
    'ott_national_series_',
    'ott_tv_genres_',
  ];
  Object.keys(localStorage)
    .filter((k) => TMDB_PREFIXES.some((p) => k.startsWith(p)))
    .forEach((k) => localStorage.removeItem(k));
}

/**
 * Clears local playback state only after Watch Sync V2 confirms watch.clear.
 */
export function clearPlaybackLocalData(): void {
  Object.keys(localStorage)
    .filter((k) =>
      k.startsWith('ott_player_watch_progress:') ||
      k.startsWith('ott_player_history:') ||
      k.startsWith('ott_player_favorites:') ||
      k.startsWith('ott_player_live_history:')
    )
    .forEach((k) => localStorage.removeItem(k));
}

/** Language/region changes invalidate the same technical data. */
export function clearMediaCache(): Promise<void> {
  return clearCatalogCache();
}
